import React, { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import Box from '@amzn/meridian/box';
import { type BoxProps } from '@amzn/meridian/box/box';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import _ from 'lodash';
import { setRestrictionListItem } from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { useAppSelector } from 'src/store/store';
import {
  WEIGHT_RESTRICTION_RANGE_NOT_SET,
  WeightRestrictionMaxRangeHandler,
  type WeightRestrictionParentTask,
} from 'src/utils/restrictionUtilsLegacy';

/**
 * Select menu for the different weight options.
 */
const WeightRestrictionMaxRangeSelector = ({ listIndex, ...otherProps }: { listIndex: number } & BoxProps) => {
  const dispatch = useDispatch();
  const { restrictionListItems, isRestrictionsPageReadOnly } = useAppSelector((state) => state.newJmrPage);
  const parentTask = restrictionListItems[listIndex] as WeightRestrictionParentTask;

  // State
  const [maxRange, setMaxRange] = useState<number>(parentTask.weightRestrictionMaxRange);
  const [options, setOptions] = useState<{ label: string; value: number }[]>([]);

  // Reinitialize the options-max-range if parent-label changes
  useEffect(() => {
    const optionsNew = WeightRestrictionMaxRangeHandler.getParentOptions(parentTask.label);
    setOptions(optionsNew);
    // Don't wanna reset the max range when preselected restrictionListItem loads,
    // but we do wanna reset the max range when a new task is selected.
    if (
      parentTask.weightRestrictionMaxRange === WEIGHT_RESTRICTION_RANGE_NOT_SET &&
      maxRange !== WEIGHT_RESTRICTION_RANGE_NOT_SET
    ) {
      setMaxRange(WEIGHT_RESTRICTION_RANGE_NOT_SET);
    }
  }, [parentTask.label]);

  // Set 0 as the value for children whose maxRanges are past the selected on
  const onChange = (value: number) => {
    setMaxRange(value);
    const parentTaskNew = _.cloneDeep(parentTask);
    for (const child of parentTaskNew.children) {
      if (child.weightRestriction.maxRange > value) {
        child.restrictedValue = '0';
      } else {
        delete child.restrictedValue;
      }
    }
    delete parentTaskNew.maxWeightRangeError;
    parentTaskNew.weightRestrictionMaxRange = value;
    dispatch(setRestrictionListItem({ item: parentTaskNew, index: listIndex }));
  };

  return (
    <Box data-testid="new-jmr__weight-restriction__max-range-selector" {...otherProps}>
      <Text>
        Associate cannot perform: <b>{parentTask?.label}</b> exceeding (lbs) *
      </Text>
      <Select
        data-cy={`new-jmr__weight__restriction__weight-range__dropdown__${listIndex}`}
        placeholder="Select..."
        value={maxRange}
        onChange={onChange}
        disabled={isRestrictionsPageReadOnly}
        errorMessage={parentTask.maxWeightRangeError}
        error={!!parentTask.maxWeightRangeError}
      >
        {options.map((option, index) => (
          <SelectOption
            label={option.label}
            value={option.value}
            key={index}
            data-cy="new-jmr__weight-restrictions__dropdown-item"
          />
        ))}
      </Select>
    </Box>
  );
};

export default WeightRestrictionMaxRangeSelector;
